import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Loader2, CheckCircle2, XCircle } from 'lucide-react';

import {
  resetPassword,
  checkTokenValid,
} from '@/store/actions/creators/authActions';
import {
  resetPasswordStatusSelector,
  resetPasswordErrorSelector,
} from '@/store/selectors/authSelector';
import { STARTED, SUCCEEDED } from '@/utils/helpers/store-helpers';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const PWD_REGEX =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[!@#$%^&*()\-_=+;:'"<,>.]).{8,}$/;

const schema = z
  .object({
    newPassword: z.string().regex(PWD_REGEX, {
      message: 'Password does not meet the requirements',
    }),
    confirmNewPassword: z.string().min(1),
  })
  .refine((data) => data.newPassword === data.confirmNewPassword, {
    path: ['confirmNewPassword'],
    message: 'Passwords must match',
  });

type FormValues = z.infer<typeof schema>;

type Checks = {
  min_char: boolean;
  one_digit: boolean;
  one_special_char: boolean;
  one_uppercase: boolean;
  one_lowercase: boolean;
};

function PasswordChecklist({ value }: { value: string }) {
  const { t } = useTranslation();
  const checks: Checks = useMemo(
    () => ({
      min_char: /^.{8,}$/.test(value),
      one_lowercase: /[a-z]/.test(value),
      one_uppercase: /[A-Z]/.test(value),
      one_digit: /[0-9]/.test(value),
      one_special_char: /[!@#$%^&*()\-_=+;:'"<,>.]/.test(value),
    }),
    [value],
  );

  const items: { key: keyof Checks; label: string }[] = [
    { key: 'min_char', label: t('REGISTRATION.AT_LEAST_8_CHARACTERS') ?? '8 characters minimum' },
    { key: 'one_uppercase', label: t('REGISTRATION.ONE_UPPERCASE') ?? 'One uppercase letter' },
    { key: 'one_lowercase', label: t('REGISTRATION.ONE_LOWERCASE') ?? 'One lowercase letter' },
    { key: 'one_digit', label: t('REGISTRATION.ONE_DIGIT') ?? 'One digit' },
    { key: 'one_special_char', label: t('REGISTRATION.ONE_SPECIAL_CHAR') ?? 'One special character' },
  ];

  return (
    <ul className="space-y-1 text-xs">
      {items.map((it) => {
        const ok = checks[it.key];
        const Icon = ok ? CheckCircle2 : XCircle;
        return (
          <li key={it.key} className={ok ? 'text-green-600' : 'text-muted-foreground'}>
            <Icon className="mr-1 inline h-3 w-3" />
            {it.label}
          </li>
        );
      })}
    </ul>
  );
}

export default function ResetPassword() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [tokenInvalid, setTokenInvalid] = useState<boolean | null>(null);

  const resetStatus = useSelector(resetPasswordStatusSelector);
  const resetError = useSelector(resetPasswordErrorSelector);

  const forgotPasswordToken = searchParams.get('token');

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmNewPassword: '' },
    mode: 'onChange',
  });

  useEffect(() => {
    (async () => {
      try {
        // @ts-expect-error legacy redux-thunk action types
        await dispatch(checkTokenValid({ token: forgotPasswordToken }));
        setTokenInvalid(false);
      } catch {
        setTokenInvalid(true);
      }
    })();
  }, []);

  const onSubmit = async (values: FormValues) => {
    try {
      await dispatch(
        // @ts-expect-error legacy redux-thunk action types
        resetPassword({
          forgotPasswordToken,
          newPassword: values.newPassword.trim(),
          confirmNewPassword: values.confirmNewPassword.trim(),
        }),
      );
    } catch {
      /* error stored in redux selector */
    }
  };

  if (resetStatus === SUCCEEDED) {
    return (
      <AuthLayout title={t('RESET_PASSWORD.CHANGE_PASSWORD')}>
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-600" />
          <p className="text-sm">{t('RESET_PASSWORD.PASSWORD_RESET_SUCCESS') ?? 'Password reset successful.'}</p>
          <Link to="/login" className="text-sm text-primary hover:underline">
            {t('COMMON.LOGIN')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (tokenInvalid) {
    return (
      <AuthLayout title={t('COMMON.TOKEN_INVALID')}>
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <XCircle className="h-12 w-12 text-destructive" />
          <Link to="/forgotpassword" className="text-sm text-primary hover:underline">
            {t('LOGIN.FORGOT_PASSWORD?')}
          </Link>
        </div>
      </AuthLayout>
    );
  }

  if (tokenInvalid === null) {
    return (
      <AuthLayout title={t('RESET_PASSWORD.CHANGE_PASSWORD')}>
        <div className="flex justify-center py-4">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AuthLayout>
    );
  }

  const newPasswordValue = form.watch('newPassword');

  return (
    <AuthLayout
      title={t('RESET_PASSWORD.CHANGE_PASSWORD')}
      description={t('RESET_PASSWORD.ENTER_NEW_PASSWORD')}
    >
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="newPassword">{t('RESET_PASSWORD.NEW_PASSWORD')}</Label>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowPassword((v) => !v)}
              className="h-auto px-2 py-1 text-xs"
            >
              {showPassword ? (
                <EyeOff className="mr-1 h-3 w-3" />
              ) : (
                <Eye className="mr-1 h-3 w-3" />
              )}
              {showPassword ? t('REGISTRATION.HIDE') : t('REGISTRATION.VIEW')}
            </Button>
          </div>
          <Input
            id="newPassword"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            {...form.register('newPassword')}
          />
        </div>

        <PasswordChecklist value={newPasswordValue} />

        <div className="space-y-2">
          <Label htmlFor="confirmNewPassword">
            {t('RESET_PASSWORD.CONFIRM_NEW_PASSWORD')}
          </Label>
          <Input
            id="confirmNewPassword"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            {...form.register('confirmNewPassword')}
          />
          {form.formState.errors.confirmNewPassword && (
            <p className="text-sm text-destructive">
              {form.formState.errors.confirmNewPassword.message}
            </p>
          )}
        </div>

        {resetError && typeof resetError === 'string' && (
          <p className="text-sm text-destructive">{resetError}</p>
        )}
        {(resetError as any)?.password && (
          <p className="text-sm text-destructive">
            {t('REGISTRATION.PASSWORDS_MUST_MEET_THE_PATTERN_AND_BE_THE_SAME')}
          </p>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={resetStatus === STARTED || !form.formState.isValid}
        >
          {resetStatus === STARTED && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {t('RESET_PASSWORD.CHANGE_PASSWORD')}
        </Button>
      </form>
    </AuthLayout>
  );
}
